import React, {useState, useEffect, useCallback, useRef} from 'react'
import {api} from './api'
import {todayStr, shiftDay, prettyDate, weekOf, nowTime, prettyTime, fmt, MACROS, fileToDataUrl} from './lib'
import {PlusIcon} from './Icons'

const hhmmFromTs = ts => {
  if (!ts) return ''
  const d = new Date(ts)
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0')
}

export default function Today() {
  const [date, setDate] = useState(todayStr())
  const [day, setDay] = useState(null)
  const [sheet, setSheet] = useState(null) // null | {mode, entry?, image?, analyze?}
  const [err, setErr] = useState('')
  const fileRef = useRef(null)

  const load = useCallback(async () => {
    try {
      setDay(await api.day(date))
    } catch (e) {
      setErr(e.message)
    }
  }, [date])

  useEffect(() => {
    load()
  }, [load])

  const goal = day?.goal || {calories: 2000, protein: 0, carbs: 0, fat: 0}
  const totals = day?.totals || {calories: 0, protein: 0, carbs: 0, fat: 0}
  const entries = day?.entries || []
  const week = weekOf(date)
  const today = todayStr()
  // calories: target = your deficit goal, maint = maintenance burn.
  // the bar scales a little past maintenance so there's headroom to "go over".
  const target = goal.calories || 0
  const maint = goal.maintenance || 0
  const scaleMax = (maint || target || 2000) * 1.12
  const calPos = v => Math.max(0, Math.min(100, (v / scaleMax) * 100))
  const overTarget = target > 0 && totals.calories > target
  const overMaint = maint > 0 && totals.calories > maint
  const fillColor = overMaint ? 'var(--danger)' : overTarget ? 'var(--warn)' : 'var(--accent)'
  const deficit = maint > 0 && target > 0 ? maint - target : 0

  const onPickFile = async e => {
    const file = e.target.files && e.target.files[0]
    e.target.value = '' // allow re-picking same file
    if (!file) return
    try {
      const image = await fileToDataUrl(file)
      setSheet({mode: 'add', image, analyze: true})
    } catch (e2) {
      setErr(e2.message)
    }
  }

  return (
    <div className="page today-page">
      {/* week strip */}
      <div className="week">
        {week.map(d => {
          const on = d.date === date
          const isToday = d.date === today
          const future = d.date > today
          return (
            <button
              key={d.date}
              className={'wk' + (on ? ' wk-on' : '') + (isToday ? ' wk-today' : '')}
              onClick={() => setDate(d.date)}
              disabled={future}>
              <span className="wk-dow">{d.dow}</span>
              <span className="wk-dom">{d.dom}</span>
            </button>
          )
        })}
      </div>

      <div className="today-head">
        <button className="icon-btn sm" onClick={() => setDate(shiftDay(date, -1))} aria-label="previous day">
          ‹
        </button>
        <div className="today-title">{prettyDate(date)}</div>
        <button
          className="icon-btn sm"
          onClick={() => setDate(shiftDay(date, 1))}
          disabled={date >= today}
          aria-label="next day">
          ›
        </button>
      </div>

      {/* daily summary */}
      <div className="summary">
        {/* calories consumed today + where that sits vs target / maintenance */}
        <div className="cal-head">
          <div className="cal-now">
            <span className="cal-now-num" style={overMaint ? {color: 'var(--danger)'} : null}>
              {fmt(totals.calories)}
            </span>
            <span className="cal-now-unit">kcal today</span>
          </div>
          {target > 0 && (
            <div className="cal-status" style={overTarget ? {color: 'var(--warn)'} : null}>
              {overTarget ? fmt(totals.calories - target) + ' over' : fmt(target - totals.calories) + ' to go'}
            </div>
          )}
        </div>

        <div className="cal-track">
          <div className="cal-fill" style={{width: calPos(totals.calories) + '%', background: fillColor}} />
          {target > 0 && <span className="cal-mark cal-mark-target" style={{left: calPos(target) + '%'}} />}
          {maint > 0 && <span className="cal-mark cal-mark-maint" style={{left: calPos(maint) + '%'}} />}
        </div>

        {(maint > 0 || target > 0) && (
          <div className="cal-lines">
            {maint > 0 && (
              <div className="cal-line">
                <span className="cal-line-k">
                  <i className="cal-tick cal-tick-maint" />
                  Maintenance
                </span>
                <span className="cal-line-v">{fmt(maint)}</span>
              </div>
            )}
            {target > 0 && (
              <div className="cal-line">
                <span className="cal-line-k">
                  <i className="cal-tick cal-tick-target" />
                  Target{deficit > 0 ? ' · −' + fmt(deficit) : ''}
                </span>
                <span className="cal-line-v">{fmt(target)}</span>
              </div>
            )}
          </div>
        )}

        {/* macros — full-width stacked bars */}
        <div className="macros">
          {MACROS.map(m => (
            <MacroBar key={m.key} label={m.label} color={m.color} value={totals[m.key]} goal={goal[m.key]} />
          ))}
        </div>
      </div>

      {/* timeline */}
      <div className="timeline">
        {entries.length === 0 && (
          <div className="empty">
            Nothing logged {date === today ? 'today' : 'this day'} yet.
            <br />
            Tap the camera to snap a meal.
          </div>
        )}
        {entries.map(e => {
          const t = e.time || hhmmFromTs(e.timestamp)
          return (
            <div key={e.eid} className="tl-row">
              <div className="tl-time">{prettyTime(t)}</div>
              <div className="tl-rail">
                <span className="tl-dot" />
              </div>
              <button className="tl-card" onClick={() => setSheet({mode: 'edit', entry: e})}>
                {e.photo ? <img className="tl-thumb" src={e.photo} alt="" /> : null}
                <div className="tl-body">
                  <div className="tl-name">{e.name}</div>
                  <div className="entry-macros">
                    {e.protein ? <span style={{color: 'var(--protein)'}}>P {e.protein}</span> : null}
                    {e.carbs ? <span style={{color: 'var(--carbs)'}}>C {e.carbs}</span> : null}
                    {e.fat ? <span style={{color: 'var(--fat)'}}>F {e.fat}</span> : null}
                  </div>
                </div>
                <div className="tl-cal">{fmt(e.calories)}</div>
              </button>
            </div>
          )
        })}
      </div>

      <div className="add-hint">
        <button className="linkish" onClick={() => setSheet({mode: 'add'})}>
          or add manually
        </button>
      </div>

      {/* camera / photo picker */}
      <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} style={{display: 'none'}} />
      <button className="fab" onClick={() => fileRef.current && fileRef.current.click()} aria-label="add a meal">
        <PlusIcon />
      </button>

      {sheet && (
        <Sheet
          date={date}
          init={sheet}
          onClose={() => setSheet(null)}
          onDone={() => {
            setSheet(null)
            load()
          }}
          onError={setErr}
        />
      )}

      {err && (
        <div className="toast" onClick={() => setErr('')}>
          {err}
        </div>
      )}
    </div>
  )
}

function MacroBar({label, color, value, goal}) {
  const has = goal > 0
  const pct = has ? Math.min((value / goal) * 100, 100) : 0
  const over = has && value > goal
  return (
    <div className="macro">
      <div className="macro-top">
        <span className="macro-label" style={{color}}>
          {label}
        </span>
        <span className="macro-val">
          {fmt(value)}
          <span className="macro-unit">g</span>
          {has && <span className="macro-goal"> / {fmt(goal)}g</span>}
        </span>
      </div>
      <div className="macro-track">
        {has ? (
          <div className="macro-fill" style={{width: pct + '%', background: over ? 'var(--danger)' : color}} />
        ) : (
          <div className="macro-track-empty" />
        )}
      </div>
    </div>
  )
}

// add / edit bottom sheet (with optional photo + AI analysis)
function Sheet({date, init, onClose, onDone, onError}) {
  const editing = init.mode === 'edit'
  const e = init.entry || {}
  const [name, setName] = useState(e.name || '')
  const [calories, setCalories] = useState(e.calories != null ? String(e.calories) : '')
  const [protein, setProtein] = useState(e.protein ? String(e.protein) : '')
  const [carbs, setCarbs] = useState(e.carbs ? String(e.carbs) : '')
  const [fat, setFat] = useState(e.fat ? String(e.fat) : '')
  const [qty, setQty] = useState('1')
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [busy, setBusy] = useState(false)
  const [analyzing, setAnalyzing] = useState(!!init.analyze)
  const [note, setNote] = useState('')
  const image = init.image || null

  // run vision analysis on the photo (once), prefill fields
  useEffect(() => {
    if (!init.analyze || !image) return
    let alive = true
    ;(async () => {
      try {
        const r = await api.analyze(image)
        if (!alive) return
        setName(r.name || '')
        setCalories(r.calories ? String(r.calories) : '')
        setProtein(r.protein ? String(r.protein) : '')
        setCarbs(r.carbs ? String(r.carbs) : '')
        setFat(r.fat ? String(r.fat) : '')
        setNote(r.note || '')
      } catch (err) {
        if (alive) setNote(err.message + ' — enter it manually below.')
      } finally {
        if (alive) setAnalyzing(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  // live search of the food library (manual add only, no photo)
  useEffect(() => {
    if (editing || image) return
    let alive = true
    const t = setTimeout(async () => {
      try {
        const {foods} = await api.foods(q.trim())
        if (alive) setResults(foods)
      } catch (err) {}
    }, 150)
    return () => {
      alive = false
      clearTimeout(t)
    }
  }, [q, editing, image])

  const pickFood = f => {
    setName(f.name)
    setCalories(String(f.calories))
    setProtein(f.protein ? String(f.protein) : '')
    setCarbs(f.carbs ? String(f.carbs) : '')
    setFat(f.fat ? String(f.fat) : '')
    setQty('1')
    setQ('')
    setResults([])
  }

  const submit = async () => {
    if (!name.trim()) return onError('name required')
    setBusy(true)
    try {
      if (editing) {
        await api.editEntry(e.eid, {name: name.trim(), calories, protein, carbs, fat})
      } else {
        await api.addEntry({date, name: name.trim(), calories, protein, carbs, fat, quantity: qty, time: nowTime(), image})
      }
      onDone()
    } catch (err) {
      onError(err.message)
      setBusy(false)
    }
  }

  const del = async () => {
    setBusy(true)
    try {
      await api.delEntry(e.eid)
      onDone()
    } catch (err) {
      onError(err.message)
      setBusy(false)
    }
  }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={ev => ev.stopPropagation()}>
        <div className="sheet-handle" />
        <div className="sheet-title">{editing ? 'Edit meal' : image ? 'New meal' : 'Add meal'}</div>

        {image && (
          <div className="sheet-photo">
            <img src={image} alt="meal" />
            {analyzing && (
              <div className="analyzing">
                <span className="spinner" /> analyzing…
              </div>
            )}
          </div>
        )}

        {!editing && !image && (
          <>
            <input className="input" placeholder="Search your foods…" value={q} onChange={ev => setQ(ev.target.value)} autoFocus />
            {results.length > 0 && (
              <div className="results">
                {results.map(f => (
                  <button key={f.fid} className="result" onClick={() => pickFood(f)}>
                    <span className="result-name">{f.name}</span>
                    <span className="result-cal">{fmt(f.calories)} kcal</span>
                  </button>
                ))}
              </div>
            )}
            <div className="sheet-sep">or enter it</div>
          </>
        )}

        <input className="input mt" placeholder="Meal name" value={name} onChange={ev => setName(ev.target.value)} />
        <div className="row">
          <Field label="Calories" value={calories} set={setCalories} />
          {!editing && !image && <Field label="Quantity" value={qty} set={setQty} step="any" />}
        </div>
        <div className="row">
          <Field label="Protein (g)" value={protein} set={setProtein} accent="var(--protein)" />
          <Field label="Carbs (g)" value={carbs} set={setCarbs} accent="var(--carbs)" />
          <Field label="Fat (g)" value={fat} set={setFat} accent="var(--fat)" />
        </div>

        {note && <div className="sheet-note">{note}</div>}

        <div className="sheet-actions">
          {editing && (
            <button className="btn btn-danger" onClick={del} disabled={busy}>
              Delete
            </button>
          )}
          <button className="btn btn-primary" onClick={submit} disabled={busy || analyzing}>
            {editing ? 'Save' : analyzing ? 'analyzing…' : 'Add meal'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({label, value, set, accent, step}) {
  return (
    <label className="field">
      <span className="field-label" style={accent ? {color: accent} : null}>
        {label}
      </span>
      <input
        className="input"
        type="number"
        inputMode="decimal"
        step={step || '1'}
        min="0"
        value={value}
        onChange={ev => set(ev.target.value)}
        placeholder="0"
      />
    </label>
  )
}
